class OPPopupBanner extends HTMLElement {
    static get observedAttributes() {
        return [
            'form-selector',
            'success-selector',
            'redirect-url',
            'redirect-delay-ms',
            'auto-open',
            'auto-open-delay-ms',
            'auto-open-scope',
            'view-threshold',
            'storage-key',
            'country-scope',
            'skip-homepage',
            'is-homepage',
            'page-type',
            'page-path',
            'repeat-on-trigger',
            'repeat-trigger-query-param',
            'repeat-trigger-cookie',
            'repeat-trigger-cookie-days',
            'allowed-page-types',
            'excluded-page-types',
            'excluded-path-fragments'
        ];
    }

    connectedCallback() {
        this._content = this.querySelector('[data-ref="content"]');
        this._closeBtns = this.querySelectorAll('[data-ref="close"]');
        this._closeBtn = this._closeBtns[0] || null;

        this._closeBtns.forEach((btn) => {
            btn.addEventListener('click', () => this.close(), { passive: true });
        });

        this._onKeydown = null;

        this._readAttrs();
        this.__openedOnce = false;
        this._observer = null;
        this._autoOpenEvaluated = false;
        this._submitHandler = null;
        this._isSubmitting = false;
        this._successHandled = false;
        this._nativeSubmitting = false;
        this._scrollLockApplied = false;

        this._securePolicyLinks();
        this._bindTargets();
        this._hideLegacyNewsletterBanners();
        this._stripNewsletterQueryParams();
        this._maybeAutoOpen();
    }

    attributeChangedCallback() {
        if (!this.isConnected) return;

        this._disconnectObserver();
        this._readAttrs();
        this._bindTargets();
        this._maybeAutoOpen();
    }

    disconnectedCallback() {
        if (this._onKeydown) {
            document.removeEventListener('keydown', this._onKeydown);
        }
        if (this._form && this._submitHandler) {
            this._form.removeEventListener('submit', this._submitHandler);
        }
        this._unlockPageScroll();
        this._disconnectObserver();
    }

    open() {
        if (this.__openedOnce) return;
        if (!this._isPageEligibleForDisplay()) return;

        this.__openedOnce = true;
        this.classList.remove('tw-hidden');
        this.setAttribute('aria-hidden', 'false');
        this._lockPageScroll();

        queueMicrotask(() => {
            if (this._closeBtn) this._closeBtn.focus();
        });
    }

    close() {
        this.classList.add('tw-hidden');
        this.setAttribute('aria-hidden', 'true');
        this._unlockPageScroll();
        this._hideLegacyNewsletterBanners();
    }

    _lockPageScroll() {
        if (this._scrollLockApplied) return;

        const body = document.body;
        const html = document.documentElement;
        if (!body || !html) return;

        const lockCount = Number.parseInt(body.getAttribute('data-op-popup-scroll-lock-count') || '0', 10) || 0;
        if (lockCount === 0) {
            OPPopupBanner._previousBodyOverflow = body.style.overflow;
            OPPopupBanner._previousHtmlOverflow = html.style.overflow;
            body.style.overflow = 'hidden';
            html.style.overflow = 'hidden';
        }

        body.setAttribute('data-op-popup-scroll-lock-count', String(lockCount + 1));
        this._scrollLockApplied = true;
    }

    _unlockPageScroll() {
        if (!this._scrollLockApplied) return;

        const body = document.body;
        const html = document.documentElement;
        if (!body || !html) {
            this._scrollLockApplied = false;
            return;
        }

        const lockCount = Number.parseInt(body.getAttribute('data-op-popup-scroll-lock-count') || '0', 10) || 0;
        const nextLockCount = Math.max(0, lockCount - 1);

        if (nextLockCount === 0) {
            body.removeAttribute('data-op-popup-scroll-lock-count');
            body.style.overflow = OPPopupBanner._previousBodyOverflow || '';
            html.style.overflow = OPPopupBanner._previousHtmlOverflow || '';
            OPPopupBanner._previousBodyOverflow = '';
            OPPopupBanner._previousHtmlOverflow = '';
        } else {
            body.setAttribute('data-op-popup-scroll-lock-count', String(nextLockCount));
        }

        this._scrollLockApplied = false;
    }

    _securePolicyLinks() {
        this.querySelectorAll('[data-popup-policy] a[href]').forEach((link) => {
            link.setAttribute('target', '_blank');

            const relValues = new Set(
                String(link.getAttribute('rel') || '')
                    .split(/\s+/)
                    .filter(Boolean)
            );
            relValues.add('noopener');
            relValues.add('noreferrer');
            link.setAttribute('rel', Array.from(relValues).join(' '));
        });
    }

    _readAttrs() {
        this._formSelector = this.getAttribute('form-selector') || '#contact_form';
        this._successSelector = this.getAttribute('success-selector') || '.newsletter-form__message--success';
        this._redirectUrl = this.getAttribute('redirect-url') || '';
        this._redirectDelayMs = parseInt(this.getAttribute('redirect-delay-ms') || '600', 10);

        this._autoOpen = (this.getAttribute('auto-open') || 'false') === 'true';
        this._autoOpenDelayMs = parseInt(this.getAttribute('auto-open-delay-ms') || '600', 10);
        this._autoOpenScope = this.getAttribute('auto-open-scope') || 'session';
        this._viewThreshold = Math.max(2, parseInt(this.getAttribute('view-threshold') || '2', 10));
        this._countryScope = this._parseCountryScope(this.getAttribute('country-scope'));
        this._skipHomepage = (this.getAttribute('skip-homepage') || 'false') === 'true';
        this._isHomepageAttr = this.getAttribute('is-homepage');
        this._pageType = String(this.getAttribute('page-type') || '').trim().toLowerCase();
        this._pagePath = String(this.getAttribute('page-path') || window.location.pathname || '').trim().toLowerCase();
        this._repeatOnTrigger = (this.getAttribute('repeat-on-trigger') || 'false') === 'true';
        this._repeatTriggerQueryParam = String(this.getAttribute('repeat-trigger-query-param') || '').trim();
        this._repeatTriggerCookie = String(this.getAttribute('repeat-trigger-cookie') || '').trim();
        this._repeatTriggerCookieDays = parseInt(this.getAttribute('repeat-trigger-cookie-days') || '30', 10);
        this._allowedPageTypes = this._parseCsvAttr(this.getAttribute('allowed-page-types'));
        this._excludedPageTypes = this._parseCsvAttr(this.getAttribute('excluded-page-types'));
        this._excludedPathFragments = this._parseCsvAttr(this.getAttribute('excluded-path-fragments'));
    }

    _bindTargets() {
        this._form = this._safeQuerySelector(this._formSelector);
        if (!this._form) return;

        this._successEl = this._form.querySelector(this._successSelector);
        this._signupStateEl = this._form.querySelector('[data-ref="signup-state"]');
        this._successStateEl = this._form.querySelector('[data-ref="success-state"]');
        this._errorEl = this._form.querySelector('[data-ref="error"]');
        this._submitBtn = this._form.querySelector('button[type="submit"]');

        this._bindInternalSubmit();

        // If Shopify returned success markers in URL, force success state.
        if (this._hasSuccessTrackingInUrl()) {
            this._clearPendingPopupSuccessFlag();
            this._showSuccessState();
            this._onSuccessDetected();
            return;
        }

        // If success state is server-rendered (no-js fallback), show it immediately.
        if (this._isVisible(this._successStateEl) || this._isVisible(this._successEl)) {
            this._clearPendingPopupSuccessFlag();
            this._showSuccessState();
            this._onSuccessDetected();
            return;
        }
    }

    _maybeAutoOpen() {
        if (!this._autoOpen || this._autoOpenEvaluated) return;
        if (!this._isCountryAllowed()) return;

        if (!this._isPageEligibleForDisplay()) return;
        if (this._skipHomepage && this._isHomepage()) return;

        const repeatTriggerValue = this._syncRepeatTriggerValue();
        const views = this._registerPageView();
        if (views < this._viewThreshold) return;
        if (repeatTriggerValue) {
            this._autoOpenEvaluated = true;
            setTimeout(() => this.open(), Math.max(0, this._autoOpenDelayMs || 0));
            return;
        }

        if (this._autoOpenScope === 'session') {
            const seenKey = this._seenKey();
            if (this._getScopedValue(seenKey) === '1') return;
            this._setScopedValue(seenKey, '1');
        }

        this._autoOpenEvaluated = true;
        setTimeout(() => this.open(), Math.max(0, this._autoOpenDelayMs || 0));
    }

    _onSuccessDetected() {
        if (this._successHandled) return;
        this._successHandled = true;
        this._setScopedValue(this._seenKey(), '1');

        this._showSuccessState();
        this.open();

        const isInternalForm = this.contains(this._form);
        if (isInternalForm && this._redirectUrl) {
            this.close();
            setTimeout(() => {
                window.location.assign(this._redirectUrl);
            }, Math.max(0, this._redirectDelayMs || 0));
        }
    }

    _bindInternalSubmit() {
        if (!this._form || !this.contains(this._form)) return;

        if (this._submitHandler) {
            this._form.removeEventListener('submit', this._submitHandler);
        }

        this._submitHandler = async (event) => {
            event.preventDefault();
            await this._handleInternalSubmit();
        };
        this._form.addEventListener('submit', this._submitHandler);
    }

    async _handleInternalSubmit() {
        if (this._isSubmitting || !this._form) return;

        this._isSubmitting = true;
        this._setSubmittingState(true);
        this._clearError();

        try {
            const result = await this._submitInternalForm();
            if (result.fallbackToNativeSubmit && this._hasShopifyCaptchaProtection()) {
                this._fallbackToNativeSubmit();
                return;
            }
            if (result.success) {
                this._clearPendingPopupSuccessFlag();
                this._showSuccessState();
                this._hideLegacyNewsletterBanners();
                this._stripNewsletterQueryParams();
                window.dispatchEvent(new CustomEvent('newsletter:success'));
                this._onSuccessDetected();
                return;
            }
            this._clearPendingPopupSuccessFlag();
            this._showError(result.error);
        } catch (_) {
            this._clearPendingPopupSuccessFlag();
            this._showError('');
        } finally {
            this._setSubmittingState(false);
            this._isSubmitting = false;
        }
    }

    _hasShopifyCaptchaProtection() {
        return Boolean(window.Shopify && window.Shopify.captcha);
    }

    _fallbackToNativeSubmit() {
        if (!this._form || this._nativeSubmitting) return;
        this._nativeSubmitting = true;
        this._setPendingPopupSuccessFlag();
        this._prepareNativeSubmitReturnTo();

        if (this._submitHandler) {
            this._form.removeEventListener('submit', this._submitHandler);
        }

        this._setSubmittingState(true);

        try {
            if (this._submitBtn && typeof this._submitBtn.click === 'function') {
                this._submitBtn.click();
            } else if (typeof this._form.requestSubmit === 'function') {
                this._form.requestSubmit();
            } else {
                this._form.submit();
            }
        } finally {
            setTimeout(() => {
                this._nativeSubmitting = false;
                this._setSubmittingState(false);
                this._bindInternalSubmit();
            }, 1200);
        }
    }

    _prepareNativeSubmitReturnTo() {
        if (!this._form) return;

        try {
            const current = new URL(window.location.href);
            current.hash = '';
            current.searchParams.delete('contact_posted');
            current.searchParams.delete('customer_posted');
            current.searchParams.delete('form_type');
            current.searchParams.set('contact_posted', 'true');

            this._upsertFormHiddenInput('return_to', `${current.pathname}${current.search}`);
        } catch (_) {
            // none
        }
    }

    _upsertFormHiddenInput(name, value) {
        if (!this._form || !name) return;

        let input = this._form.querySelector(`input[name="${name}"]`);
        if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            this._form.appendChild(input);
        }

        input.value = String(value || '');
    }

    async _submitInternalForm() {
        const action = this._resolveFormAction();
        const formData = new FormData(this._form);
        const body = new URLSearchParams();

        for (const [key, value] of formData.entries()) {
            if (typeof value === 'string') {
                body.append(key, value);
            }
        }

        const response = await fetch(action, {
            method: 'POST',
            credentials: 'same-origin',
            redirect: 'manual',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
                Accept: 'text/html',
                'X-Requested-With': 'XMLHttpRequest'
            },
            body: body.toString()
        });

        if (this._isRedirectResponse(response)) {
            return { success: true, error: '' };
        }

        const html = await response.text();
        const url = response && response.url ? response.url : '';
        const status = response ? Number(response.status || 0) : 0;
        return this._parseFormSubmitResult(html, url, status);
    }

    _resolveFormAction() {
        const fallback = '/contact';
        const raw = this._form && this._form.getAttribute('action') ? this._form.getAttribute('action') : fallback;

        try {
            const parsed = new URL(raw, window.location.origin);
            parsed.hash = '';
            return `${parsed.pathname}${parsed.search}`;
        } catch (_) {
            return fallback;
        }
    }

    _parseFormSubmitResult(html, responseUrl, responseStatus) {
        if (this._responseHasSuccessMarker(responseUrl)) {
            return { success: true, error: '' };
        }

        const rawHtml = String(html || '');
        const isCaptchaFailure = this._isCaptchaFailureResponse(responseStatus, rawHtml);

        let parsedDoc = null;
        try {
            parsedDoc = new DOMParser().parseFromString(rawHtml, 'text/html');
        } catch (_) {
            parsedDoc = null;
        }

        if (!parsedDoc) {
            return {
                success: false,
                error: '',
                fallbackToNativeSubmit: isCaptchaFailure
            };
        }

        const formId = this._form && this._form.id ? this._form.id : '';
        const scope = formId ? parsedDoc.getElementById(formId) || parsedDoc : parsedDoc;

        const successNode = scope.querySelector(this._successSelector);
        if (successNode || this._isRenderedSuccessState(scope)) {
            return { success: true, error: '' };
        }

        const errorNode =
            scope.querySelector('[data-ref="error"]') ||
            scope.querySelector('.form__message') ||
            scope.querySelector('.newsletter-form__message');

        const errorText = errorNode ? String(errorNode.textContent || '').trim() : '';
        return {
            success: false,
            error: errorText,
            fallbackToNativeSubmit: isCaptchaFailure
        };
    }

    _isCaptchaFailureResponse(responseStatus, html) {
        const status = Number(responseStatus || 0);
        if (status !== 400 && status !== 422) return false;

        const normalized = String(html || '').toLowerCase();
        if (!normalized.includes('captcha')) return false;

        return (
            normalized.includes('missing captcha token') ||
            normalized.includes('invalid captcha token') ||
            normalized.includes('captcha token')
        );
    }

    _isRedirectResponse(response) {
        if (!response) return false;
        if (response.type === 'opaqueredirect') return true;

        const status = Number(response.status || 0);
        return status >= 300 && status < 400;
    }

    _responseHasSuccessMarker(responseUrl) {
        const normalized = String(responseUrl || '');
        if (!normalized) return false;

        const markers = ['contact_posted=true', 'customer_posted=true'];
        return markers.some((marker) => normalized.includes(marker));
    }

    _isRenderedSuccessState(scope) {
        if (!scope) return false;

        const successStateNode = scope.querySelector('[data-ref="success-state"]');
        if (!successStateNode) return false;
        if (successStateNode.hasAttribute('hidden')) return false;
        if (successStateNode.classList.contains('tw-hidden')) return false;
        return successStateNode.getAttribute('aria-hidden') !== 'true';
    }

    _hasSuccessTrackingInUrl() {
        try {
            const url = new URL(window.location.href);
            if (!this._hasPendingPopupSuccessFlag()) return false;
            return (
                url.searchParams.get('contact_posted') === 'true' ||
                url.searchParams.get('customer_posted') === 'true'
            );
        } catch (_) {
            return false;
        }
    }

    _pendingPopupSuccessKey() {
        return `op_pb_pending_success__${this._storageBaseKey()}`;
    }

    _setPendingPopupSuccessFlag() {
        this._setSession(this._pendingPopupSuccessKey(), '1');
    }

    _hasPendingPopupSuccessFlag() {
        return this._getSession(this._pendingPopupSuccessKey()) === '1';
    }

    _clearPendingPopupSuccessFlag() {
        try {
            window.sessionStorage.removeItem(this._pendingPopupSuccessKey());
        } catch (_) {
            // none
        }
    }

    _setSubmittingState(isSubmitting) {
        if (!this._submitBtn) return;
        this._submitBtn.disabled = isSubmitting;
        this._submitBtn.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');
        this._submitBtn.classList.toggle('tw-opacity-70', isSubmitting);
    }

    _showSuccessState() {
        if (this._signupStateEl) this._signupStateEl.classList.add('tw-hidden');
        if (this._successStateEl) this._successStateEl.classList.remove('tw-hidden');
    }

    _showError(message) {
        if (!this._errorEl) return;
        const fallback = this._errorEl.getAttribute('data-default-message') || 'Please enter a valid email address.';
        const nextMessage = message && String(message).trim() ? String(message).trim() : fallback;
        this._errorEl.textContent = nextMessage;
        this._errorEl.classList.remove('tw-hidden');
    }

    _clearError() {
        if (!this._errorEl) return;
        this._errorEl.classList.add('tw-hidden');
    }

    _hideLegacyNewsletterBanners() {
        const legacyBanners = document.querySelectorAll('.newsletter-subscribe');
        legacyBanners.forEach((banner) => banner.classList.add('tw-hidden'));
    }

    _stripNewsletterQueryParams() {
        try {
            const url = new URL(window.location.href);
            const hadTracking =
                url.searchParams.has('contact_posted') ||
                url.searchParams.has('form_type') ||
                url.searchParams.has('customer_posted');
            const hadHash = url.hash && url.hash.includes('contact_form');

            if (!hadTracking && !hadHash) return;

            url.searchParams.delete('contact_posted');
            url.searchParams.delete('form_type');
            url.searchParams.delete('customer_posted');
            if (hadHash) url.hash = '';

            window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
        } catch (_) {
            // none
        }
    }

    _disconnectObserver() {
        if (this._observer) {
            this._observer.disconnect();
            this._observer = null;
        }
    }

    _isVisible(el) {
        if (!el) return false;
        const style = getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity || '1') === 0) return false;
        return el.offsetParent !== null || style.position === 'fixed';
    }

    _isHomepage() {
        if (this._isHomepageAttr === 'true') return true;
        if (this._isHomepageAttr === 'false') return false;

        const currentPath = (window.location.pathname || '').replace(/\/+$/, '');
        const rootPath = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/+$/, '');
        return currentPath === '' || currentPath === rootPath;
    }

    _isPageEligibleForDisplay() {
        let pageType = String(this._pageType || '').trim().toLowerCase();
        const pagePath = String(this._pagePath || '').trim().toLowerCase();

        // Defensive fallback: treat homepage as "index" even when page-type isn't exposed.
        if (!pageType && this._isHomepage()) {
            pageType = 'index';
        }

        if (pageType && this._excludedPageTypes.includes(pageType)) return false;

        for (const pathFragment of this._excludedPathFragments) {
            if (pathFragment && pagePath.includes(pathFragment)) return false;
        }

        if (!this._allowedPageTypes || this._allowedPageTypes.length === 0) return true;
        return this._allowedPageTypes.includes(pageType);
    }

    _registerPageView() {
        const key = this._viewKey();
        const pageKey = this._viewPageKey();
        const currentPage = this._currentPageViewId();
        const previousPage = this._getScopedValue(pageKey);

        if (previousPage === currentPage) {
            const existing = Number.parseInt(this._getScopedValue(key) || '0', 10);
            return Number.isFinite(existing) ? existing : 0;
        }

        const raw = this._getScopedValue(key) || '0';
        const current = Number.parseInt(raw, 10);
        const next = Number.isFinite(current) ? current + 1 : 1;
        this._setScopedValue(key, String(next));
        this._setScopedValue(pageKey, currentPage);
        return next;
    }

    _viewKey() {
        return `op_pb_views__${this._storageBaseKey()}`;
    }

    _viewPageKey() {
        return `op_pb_last_page__${this._storageBaseKey()}`;
    }

    _currentPageViewId() {
        try {
            const url = new URL(window.location.href);
            url.hash = '';
            url.searchParams.delete('contact_posted');
            url.searchParams.delete('customer_posted');
            url.searchParams.delete('form_type');
            return `${url.pathname}${url.search}`.toLowerCase();
        } catch (_) {
            return String(window.location && window.location.pathname ? window.location.pathname : '').toLowerCase();
        }
    }

    _seenKey() {
        return `op_pb_seen__${this._storageBaseKey()}`;
    }

    _storageBaseKey() {
        return this.getAttribute('storage-key') || this.getAttribute('id') || 'op-popup';
    }

    _repeatTriggerSessionKey() {
        return `op_pb_repeat_trigger__${this._storageBaseKey()}`;
    }

    _syncRepeatTriggerValue() {
        if (!this._repeatOnTrigger) return '';

        const fromUrl = this._normalizeTriggerValue(this._getQueryParamValue(this._repeatTriggerQueryParam));
        if (fromUrl) {
            this._persistRepeatTriggerValue(fromUrl);
            return fromUrl;
        }

        const fromSession = this._normalizeTriggerValue(this._getSession(this._repeatTriggerSessionKey()));
        if (fromSession) return fromSession;

        const fromCookie = this._normalizeTriggerValue(this._getCookie(this._repeatTriggerCookie));
        if (fromCookie) {
            this._setSession(this._repeatTriggerSessionKey(), fromCookie);
            return fromCookie;
        }

        return '';
    }

    _persistRepeatTriggerValue(value) {
        const normalized = this._normalizeTriggerValue(value);
        if (!normalized) return;

        this._setSession(this._repeatTriggerSessionKey(), normalized);

        if (this._repeatTriggerCookie) {
            this._setCookie(this._repeatTriggerCookie, normalized, this._repeatTriggerCookieDays);
        }
    }

    _normalizeTriggerValue(value) {
        const normalized = String(value || '').trim();
        return normalized || '';
    }

    _getQueryParamValue(key) {
        if (!key) return '';

        try {
            return new URL(window.location.href).searchParams.get(key) || '';
        } catch (_) {
            return '';
        }
    }

    _parseCountryScope(raw) {
        if (!raw) return [];
        return raw
            .split(',')
            .map((code) => this._normalizeCountryCode(code))
            .filter(Boolean);
    }

    _normalizeCountryCode(code) {
        if (!code) return '';
        const normalized = String(code).trim().toUpperCase();
        if (!normalized) return '';
        if (normalized === 'UK') return 'GB';
        return normalized;
    }

    _isCountryAllowed() {
        if (!this._countryScope || this._countryScope.length === 0) return true;
        const candidates = this._collectCountryCandidates();
        for (const code of candidates) {
            if (this._countryScope.includes(code)) return true;
        }
        return false;
    }

    _collectCountryCandidates() {
        const out = new Set();
        const add = (value) => {
            const normalized = this._normalizeCountryCode(value);
            if (normalized) out.add(normalized);
        };

        add(window.Shopify && window.Shopify.country);

        if (window.SHOP_LOCALES && window.SHOP_LOCALES.current && window.SHOP_LOCALES.current.iso_code) {
            const parts = String(window.SHOP_LOCALES.current.iso_code).split('-');
            if (parts.length > 1) add(parts[parts.length - 1]);
        }

        const htmlLang = document.documentElement && document.documentElement.lang;
        if (htmlLang) {
            const langParts = String(htmlLang).split('-');
            if (langParts.length > 1) add(langParts[langParts.length - 1]);
        }

        const path = window.location && window.location.pathname ? window.location.pathname : '';
        const firstSegment = path.split('/').filter(Boolean)[0] || '';
        const match = firstSegment.match(/^([a-z]{2})[-_]([a-z]{2})$/i);
        if (match) add(match[2]);

        return Array.from(out);
    }

    _parseCsvAttr(raw) {
        if (!raw) return [];
        return String(raw)
            .split(',')
            .map((entry) => String(entry || '').trim().toLowerCase())
            .filter(Boolean);
    }

    _setScopedValue(key, value) {
        if (this._setSession(key, value)) return;
        this._setCookie(key, value);
    }

    _getScopedValue(key) {
        const fromSession = this._getSession(key);
        if (fromSession !== null) return fromSession;
        return this._getCookie(key);
    }

    _setSession(key, value) {
        try {
            window.sessionStorage.setItem(key, value);
            return true;
        } catch (_) {
            return false;
        }
    }

    _getSession(key) {
        try {
            return window.sessionStorage.getItem(key);
        } catch (_) {
            return null;
        }
    }

    _setCookie(name, value, days) {
        try {
            let expires = '';
            if (Number.isFinite(days) && days > 0) {
                const d = new Date();
                d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
                expires = `; expires=${d.toUTCString()}`;
            }
            document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}${expires}; path=/; SameSite=Lax`;
        } catch (_) {
            // none
        }
    }

    _getCookie(name) {
        try {
            const needle = `${encodeURIComponent(name)}=`;
            const parts = document.cookie.split(';');
            for (let cookie of parts) {
                while (cookie.charAt(0) === ' ') cookie = cookie.substring(1);
                if (cookie.indexOf(needle) === 0) {
                    return decodeURIComponent(cookie.substring(needle.length, cookie.length));
                }
            }
        } catch (_) {
            // none
        }
        return null;
    }

    _safeQuerySelector(selector) {
        if (!selector) return null;
        try {
            return document.querySelector(selector);
        } catch (_) {
            return null;
        }
    }
}

if (!customElements.get('op-popup-banner')) {
    customElements.define('op-popup-banner', OPPopupBanner);
}
